import { useEffect, useState } from "react";

import {
  isProofWindowProjection,
  ProofWindowProjection,
  ProofWindowResponse,
} from "../proof-window";
import { Panel } from "./Panel";

type LoadState = "LOADING" | "LIVE" | "DEGRADED";

export function ProofWindowPanel() {
  const [rows, setRows] = useState<ProofWindowProjection[]>([]);
  const [loadState, setLoadState] = useState<LoadState>("LOADING");

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/proof-window", {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Proof Window API ${response.status}`);
        return (await response.json()) as ProofWindowResponse;
      })
      .then((payload) => {
        if (
          payload.schema_version !== "aevion.proof-window-api.v0.1" ||
          !Array.isArray(payload.rows) ||
          !payload.rows.every(isProofWindowProjection)
        ) {
          throw new Error("Proof Window API returned a non-conforming projection");
        }
        setRows(payload.rows);
        setLoadState("LIVE");
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setRows([]);
        setLoadState("DEGRADED");
      });
    return () => controller.abort();
  }, []);

  return (
    <Panel
      title={`Proof Window · ${loadState}`}
      claimCeiling="SANITIZED_READ_MODEL_NOT_RAW_EVIDENCE"
    >
      {rows.length === 0 ? (
        <p className="empty-state">
          {loadState === "DEGRADED"
            ? "API unavailable. No fixture result is being represented as live evidence."
            : "No projected missions."}
        </p>
      ) : (
        <div className="proof-grid" role="table" aria-label="Proof Window missions">
          {rows.map((row) => (
            <article
              className={`proof-card proof-card--${row.mission_state.toLowerCase()}`}
              key={row.mission_id}
            >
              <div className="proof-card__state">{row.mission_state}</div>
              <h3>{row.public_title}</h3>
              <dl>
                <div><dt>Gate</dt><dd>{row.gate_decision}</dd></div>
                <div><dt>Evidence</dt><dd>{row.evidence_state}</dd></div>
                <div><dt>Mandatory</dt><dd>{row.mandatory_passed}/{row.mandatory_total}</dd></div>
                <div><dt>Cost</dt><dd>${row.cost_usd.toFixed(4)}</dd></div>
              </dl>
              {row.hold_reason_code ? (
                <p className="hold-reason">{row.hold_reason_code}</p>
              ) : null}
              <code title={row.last_receipt_sha256}>
                {row.last_receipt_sha256.slice(0, 16)}…
              </code>
            </article>
          ))}
        </div>
      )}
    </Panel>
  );
}
